/* eslint-disable react/prop-types */
import React, { useState, useCallback } from 'react';
import classNames from 'classnames';
import _ from 'lodash';
import { Link } from 'react-router';
import { SegmentUiElements, SegmentPageActionTypes } from '@foxcorp/delta-web-analytics';
import { SHOW_STATE_CLIP } from 'helpers/BadgeHelper';
import { helperGetRelativeUrl, LAST_SESSION_PATH } from 'helpers/RouteHelper';
import { COLLECTION_TYPE_GENRE } from 'helpers/ContentHelper';
import { setSessionStorageItem } from 'helpers/Storage';
import { FAVORITE_PLUS_HOVER } from 'components/Favorite';
import Badge from 'components/Badge';
import {
  ActionButtons,
  Detail,
  NetworkBubble,
  Overlay,
  PlayButton,
  ProgressBar,
  Subtitle,
  TileImage,
  TileTitle,
} from '../../components';
import Style from '../../Styles/LiveTile.module.scss';

const LiveTile = ({
  account,
  altText,
  badge,
  callSign,
  collectionType,
  disableHoverStateActionButtons,
  endDate,
  favoritesManager,
  freemiumOverride,
  gridIndex,
  gridTitle,
  hoverImage,
  image,
  isAnonymous,
  isDisabled,
  isHoverStateV2,
  isRedirect,
  isSeries,
  isSeriesDetailAvailable,
  isUpcoming,
  itemIndex,
  link,
  name,
  network,
  onTileClick,
  onTitleClick,
  originalItem,
  overlayProps,
  percentWatched,
  recommendationIdData,
  scaleFromLeft,
  scaleFromRight,
  segmentPlaylistButtonAction,
  seriesName,
  seriesScreenUrl,
  showCode,
  showLiveProgressBar,
  startDate,
  subtitle,
  subtitleLines,
  subtitleText,
  subtitleText: subtitleTextProp,
  target,
  theme,
  title,
  titleLines,
  titleLink,
  titleText,
  truncateSubtitle,
  videoType,
}) => {
  const [favorited, setFavorited] = useState(false);
  const titleLinkVal = titleLink || link;
  const imageContainerClasses = [Style.imageContainer, Style.maintainAspectRatio];
  if (scaleFromLeft) imageContainerClasses.push(Style.scaleFromLeft);
  if (scaleFromRight) imageContainerClasses.push(Style.scaleFromRight);

  const onSubtitleOnClick = useCallback(() => {
    if (onTileClick) {
      onTileClick({
        originalItem,
        name,
        percentWatched,
        account,
        recommendationIdData,
        subtitleText: subtitleTextProp,
        titleText,
        uiElement: SegmentUiElements.SERIES_EPISODE,
      });
    }
  }, [
    onTileClick,
    originalItem,
    name,
    percentWatched,
    account,
    subtitleTextProp,
    titleText,
    recommendationIdData,
  ]);

  const onTileTitleClick = useCallback(() => {
    setSessionStorageItem({ key: LAST_SESSION_PATH, value: window.location.pathname });
    const commonArgs = {
      account,
      gridIndex,
      gridTitle,
      itemIndex,
      name,
      network,
      originalItem,
      percentWatched,
      recommendationIdData,
      subtitleText,
      title,
      titleText,
    };
    onTileClick({ ...commonArgs, uiElement: SegmentUiElements.SERIES_EPISODE });
    if (onTitleClick) {
      onTitleClick({ ...commonArgs, uiElement: SegmentUiElements.SERIES_TITLE });
    }
  }, [
    account,
    gridIndex,
    gridTitle,
    itemIndex,
    name,
    network,
    originalItem,
    onTitleClick,
    onTileClick,
    percentWatched,
    recommendationIdData,
    subtitleText,
    title,
    titleText,
  ]);

  const onMoreInfoClick = () => {
    segmentPlaylistButtonAction({
      actionType: SegmentPageActionTypes.MORE_EPISODES,
      uiElement: SegmentUiElements.INFO,
    });
  };

  const titleOptions = {
    account,
    gridIndex,
    gridTitle,
    isDisabled,
    isHoverStateV2,
    isRedirect,
    isSeriesDetailAvailable,
    isUpcoming,
    itemIndex,
    link: titleLinkVal,
    name,
    network,
    onClick: onTileTitleClick,
    originalItem,
    percentWatched,
    recommendationIdData,
    Style,
    subtitleText,
    target,
    title,
    titleLines,
    titleText,
  };

  const favorites = _.get(favoritesManager, 'favorites');
  const moreInfoText = videoType === 'clip' ? 'More Info' : 'More Episodes';
  const seriesUrl = seriesScreenUrl ? helperGetRelativeUrl({ url: seriesScreenUrl }) : titleLinkVal;
  const isGenre = collectionType === COLLECTION_TYPE_GENRE;
  const isFavorited = favorited || _.find(favorites, ({ favoriteID }) => favoriteID === showCode);

  const favoriteData = {
    ...(overlayProps?.favoriteData ?? {}),
    titleText: seriesName,
    subtitleText: subtitleTextProp,
  };

  return (
    <div className={theme.tile}>
      <Link
        to={isSeries ? titleLinkVal : link}
        target={target}
        className={classNames(imageContainerClasses)}
      >
        <TileImage image={image || hoverImage} altText={altText} rendered Style={Style} />
        <div className={Style.liveTileShadow} />
        <Badge theme={Style} badge={badge} />
        <ProgressBar
          endDate={endDate}
          percentWatched={percentWatched}
          showLiveProgressBar={showLiveProgressBar}
          startDate={startDate}
        />
        <Overlay Style={Style}>
          <ActionButtons
            favoriteData={favoriteData}
            favoritesManager={favoritesManager}
            handleSetFavorite={fav => setFavorited(!!fav)}
            hasSeries={!isSeries && isSeriesDetailAvailable !== false}
            hoverStateWatchlistText={isFavorited ? 'Added to Watchlist' : 'Add to Watchlist'}
            iconType={FAVORITE_PLUS_HOVER}
            isOnlyInfo={!favorites || badge === SHOW_STATE_CLIP}
            isOnlyWatchList={isSeries || isSeriesDetailAvailable === false}
            link={seriesUrl}
            moreInfoAria={`${moreInfoText} ${seriesName}`}
            moreInfoText={moreInfoText}
            onMoreInfoClick={onMoreInfoClick}
            render={!isGenre && !disableHoverStateActionButtons && !isAnonymous}
            segmentPlaylistButtonAction={segmentPlaylistButtonAction}
          />
          <PlayButton render={!isGenre} link={isSeries ? titleLinkVal : link} />
        </Overlay>
      </Link>
      <Detail Style={Style}>
        <div className={classNames([Style.info, Style.infoExtended])} aria-hidden="true">
          <NetworkBubble callSign={callSign} link={link} showNetworkIcon />
          <div className={Style.rightDetails} aria-hidden="true" aria-live="off">
            <TileTitle {...titleOptions} />
            <Subtitle
              freemiumOverride={freemiumOverride}
              isDisabled={false}
              isFreemiumOverride={false}
              isRedirect={isRedirect}
              link={link}
              onClick={onSubtitleOnClick}
              subtitle={subtitle}
              subtitleLines={subtitleLines}
              target={target}
              theme={Style}
              truncateSubtitle={truncateSubtitle}
            />
          </div>
        </div>
      </Detail>
    </div>
  );
};

export default LiveTile;
